/**
 *
 * ActionButtons
 *
 */

import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _get from 'lodash/get';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import MixPanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import WithSizes from 'react-sizes';
import DownloadPdfButton from './components/DownloadPdfButton';
import RetakeButton from './components/RetakeButton';
import DownloadCertificateButton from './components/DownloadCertificateButton';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: props => (props.isVariantB ? 50 : 20),
    marginBottom: props => (props.isVariantB ? 0 : 20),
    paddingBottom: props => (props.isVariantB ? 32 : 0),
    borderBottom: props => (props.isVariantB ? '1px solid #E9ECED' : 'none'),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    listStyleType: 'none',
  },
  assessmentsButton: {
    display: 'flex',
    padding: props => (props.isVariantB ? '10px 15px' : '12px 40px'),
    borderRadius: 4,
    justifyContent: 'center',
    marginLeft: 8,
    marginRight: 8,
    textTransform: 'none',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
      width: '100%',
      marginTop: 16,
    },
    ...theme.typography.caption,
  },
}));

function ActionButtons({
  answer,
  assessment,
  handlePdfContent,
  handleEmailResults,
  isTablet,
  uiOverrides = {},
  customElements = {},
}) {
  const history = useHistory();
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const clientDetails = useSelector(makeSelectClientDetails());
  const { accessCode, isVariantB, shouldABTest } = useContext(
    AssessmentResultContext,
  );
  const classes = useStyles({ isVariantB });
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const { slug } = useParams();
  const showClinicalViewButton = _get(
    clientDetails,
    'metadata.showClinicalViewButton',
    false,
  );

  const assessmentID = _get(assessment, 'sys.id', '');
  const name = _get(assessment, 'fields.name', '');

  const clientBrand = _get(clientDetails, 'shortName');

  // Get custom class names or use defaults
  const containerClassName =
    uiOverrides.containerClassName || classes.container;
  const buttonClassName =
    uiOverrides.assessmentsButtonClassName || classes.assessmentsButton;

  const trackClick = action => {
    MixPanel.track(
      action,
      injectAssessmentResultsABData(
        {
          slug,
          assessmentName: name,
          assessmentId: assessmentID,
          page: window.location.pathname,
          path: 'assessment results',
          type: 'bottom cta',
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  // Handle email click with tracking
  const handleEmailClick = () => {
    const topic =
      clientBrand === 'wellscreenmonterey'
        ? 'Assessment Referral - Email Results - Clicked'
        : 'Assessment Results - Email Results - Clicked';
    trackClick(topic);
    handleEmailResults();
  };

  // Handle clinical report click with tracking
  const handleClinicalClick = () => {
    trackClick('Assessment Results - Clinical Report - Clicked');
    history.push(`/assessments/clinicalresults/${accessCode}`);
  };

  // Wrap component with UI override if available
  const wrapWithUIOverride = (type, component) => {
    const Wrapper = uiOverrides[`${type}Wrapper`];
    return Wrapper ? <Wrapper>{component}</Wrapper> : component;
  };

  // Create email button element
  const emailButtonText = _get(
    assessmentResultsSiteCopy,
    'pageCopy.emailResultsLabel',
    '',
  );
  const formattedText =
    typeof emailButtonText === 'string'
      ? emailButtonText
          .toLowerCase()
          .charAt(0)
          .toUpperCase() + emailButtonText.toLowerCase().slice(1)
      : emailButtonText;
  const allowGuestEmail = _get(
    clientDetails,
    'metadata.assessmentResults.allowGuestEmail',
    false,
  );
  const emailButton =
    allowGuestEmail ||
    (allowSignIn &&
    !_get(clientDetails, 'metadata.hideEmailMyResultsButton', false)
      ? wrapWithUIOverride(
          'Email',
          <Button
            variant="contained"
            color="primary"
            className={buttonClassName}
            onClick={handleEmailClick}
          >
            {formattedText}
          </Button>,
        )
      : null);

  // Create download button element
  const downloadButton = wrapWithUIOverride(
    'DownloadPdf',
    <DownloadPdfButton
      answer={answer}
      handlePdfContent={handlePdfContent}
      tight={isVariantB}
      variant="outlined"
    />,
  );

  // Create retake button element
  const retakeButton = wrapWithUIOverride(
    'Retake',
    <RetakeButton
      variant="outlined"
      tight={isVariantB}
      trackingProps={{
        placement: 'bottom',
        assessmentName: name,
        assessmentId: assessmentID,
      }}
    />,
  );

  // Create clinical report button element
  const clinicalButton =
    isTablet &&
    showClinicalViewButton &&
    wrapWithUIOverride(
      'Clinical',
      <Button
        variant="outlined"
        color="primary"
        className={buttonClassName}
        onClick={handleClinicalClick}
      >
        {_get(
          assessmentResultsSiteCopy,
          'pageCopy.viewClinicalReportLabel',
          'View Clinical Report',
        )}
      </Button>,
    );

  // Create certificate button element
  const certificateButton = wrapWithUIOverride(
    'DownloadCertificate',
    <DownloadCertificateButton
      answer={answer}
      variant="outlined"
      tight={isVariantB}
    />,
  );

  // Create button elements map
  const buttonElements = {
    email: customElements.email || (emailButton && <>{emailButton}</>),
    download: customElements.download || <>{downloadButton}</>,
    certificate: customElements.certificate || <>{certificateButton}</>,
    retake: customElements.retake || <>{retakeButton}</>,
    clinical:
      clinicalButton && (customElements.clinical || <>{clinicalButton}</>),
  };

  // Use custom button order if provided, otherwise use default order
  const buttonOrder = uiOverrides.buttonOrder || [
    'email',
    'download',
    'certificate',
    'retake',
    'clinical',
  ];

  const renderContent = () =>
    buttonOrder.map(type => buttonElements[type]).filter(Boolean);

  return (
    <ul className={containerClassName} id="assessment-action-buttons">
      {renderContent().map((button, idx) => (
        <li key={idx}>{button}</li>
      ))}
    </ul>
  );
}

const mapSizesToProps = ({ width }) => ({
  isTablet: width < 960,
});

export default WithSizes(mapSizesToProps)(ActionButtons);
